import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Checkbox, Flex, Input, Modal, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";

// The filters as saved: the query string without paging, keys in order.
function normal(search: string): string {
  const p = new URLSearchParams(search);
  ["page", "size", "offset"].forEach((k) => p.delete(k));
  p.sort();
  return p.toString();
}

/**
 * Quick Query: a list's filters saved under a name (the member's own, or
 * shared with the team), one click to apply. Works on any list whose filters
 * live in the URL.
 */
export function SavedViews({ page }: { page: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const key = ["saved-views", page];
  const views = useQuery({ queryKey: key, queryFn: () => unwrap(api.GET("/v1/saved-views", { params: { query: { page } } })) });
  const current = normal(location.search);
  const save = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/saved-views", { body: { page, name: name.trim(), query: current, shared } })),
    onSuccess: () => {
      void message.success(t("views.saved", { name: name.trim() }));
      void queryClient.invalidateQueries({ queryKey: key });
      setSaving(false);
      setName("");
      setShared(false);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/saved-views/{viewID}", { params: { path: { viewID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => void message.error(errorText(err)),
  });
  const items = views.data?.items ?? [];
  return (
    <Flex gap={6} wrap align="center" style={{ marginBottom: 12 }}>
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {t("views.title")}
      </Typography.Text>
      {items.map((v) => {
        const active = normal(v.query) === current;
        const removable = v.mine || (v.shared && can("warehouse.manage"));
        return (
          <Tag.CheckableTag
            key={v.id}
            checked={active}
            onChange={() => navigate(`${location.pathname}${v.query ? `?${v.query}` : ""}`)}
            style={{ border: "1px solid var(--ant-color-border)", padding: "2px 8px" }}
          >
            {v.shared && (
              <Tooltip title={t("views.sharedBy", { name: v.userName })}>
                <TeamOutlined style={{ marginRight: 4 }} />
              </Tooltip>
            )}
            {v.name}
            {removable && (
              <span
                role="button"
                aria-label={t("common.delete")}
                style={{ marginLeft: 6, opacity: 0.6 }}
                onClick={(e) => {
                  e.stopPropagation();
                  modal.confirm({
                    title: t("views.deleteTitle", { name: v.name }),
                    okText: t("common.delete"),
                    okButtonProps: { danger: true },
                    cancelText: t("common.cancel"),
                    onOk: () => remove.mutateAsync(v.id),
                  });
                }}
              >
                ×
              </span>
            )}
          </Tag.CheckableTag>
        );
      })}
      <Button size="small" type="dashed" icon={<PlusOutlined />} disabled={!current} onClick={() => setSaving(true)}>
        {t("views.save")}
      </Button>
      <Modal
        open={saving}
        title={t("views.save")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        okButtonProps={{ disabled: !name.trim() }}
        confirmLoading={save.isPending}
        onOk={() => save.mutate()}
        onCancel={() => setSaving(false)}
        destroyOnHidden
      >
        <Flex vertical gap={12}>
          <Typography.Text type="secondary">{t("views.saveHelp")}</Typography.Text>
          <Input autoFocus maxLength={60} placeholder={t("views.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} onPressEnter={() => name.trim() && save.mutate()} />
          <Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)}>
            {t("views.share")}
          </Checkbox>
        </Flex>
      </Modal>
    </Flex>
  );
}
